// QR Kiosk v2 — KV2-2 file chip (designer FileChip). Kiosk review §3 (functional):
// IMAGE attachments open IN-PHONE — tap → fullscreen lightbox (the committed
// MidasImageViewer atom, reused: fixed layer, object-fit contain, tap/✕ closes).
// Non-image files keep the v1 download semantics (a real <a> in a new tab).
// Falsy content degrades to a non-interactive <div> (never a crashing/empty link).
import React, { useState } from "react";
import { Glyph, GD } from "../shared/glyphs";
import { usePressed } from "../shared/usePressed";
import { MidasImageViewer } from "../midas/MidasImageViewer";
import { isImageFileName } from "./reportUtils";
import { ServiceReportFile } from "../../models/serviceReportsModels";

export interface ReportFileChipProps {
  file: ServiceReportFile;
  /** §7.3: when set and the file is an image-with-content, tap opens the parent's
   *  PhotoCarousel instead of the internal single-image lightbox (kept as the fallback
   *  for other consumers). */
  onOpenImage?: () => void;
}

export function ReportFileChip({ file, onOpenImage }: ReportFileChipProps) {
  const { pressHandlers, pressStyle } = usePressed();
  const [viewerOpen, setViewerOpen] = useState(false);
  const isImg = isImageFileName(file.name);

  const baseStyle: React.CSSProperties = {
    display: "flex",
    alignItems: "center",
    gap: 9,
    height: 52,
    padding: "0 11px",
    borderRadius: 10,
    border: "1px solid var(--border)",
    background: "var(--bg-soft)",
    minWidth: 0,
    textDecoration: "none",
  };

  const inner = (
    <>
      <span
        style={{
          width: 32,
          height: 32,
          borderRadius: 7,
          background: isImg ? "color-mix(in oklab, var(--faint) 22%, transparent)" : "var(--card)",
          border: "1px solid var(--border-soft)",
          display: "inline-grid",
          placeItems: "center",
          color: "var(--muted)",
          flexShrink: 0,
        }}
      >
        <Glyph d={isImg ? GD.cam : GD.doc} size={16} />
      </span>
      <span
        style={{
          fontSize: 13,
          fontWeight: 600,
          color: "var(--ink-2)",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {file.name}
      </span>
    </>
  );

  // Image with content → parent carousel (§7.3) when wired, else in-page lightbox fallback.
  if (file.content && isImg) {
    return (
      <>
        <button
          type="button"
          onClick={() => (onOpenImage ? onOpenImage() : setViewerOpen(true))}
          {...pressHandlers}
          style={{ ...baseStyle, ...pressStyle, width: "100%", fontFamily: "inherit", cursor: "zoom-in" }}
        >
          {inner}
        </button>
        {!onOpenImage && viewerOpen && (
          <MidasImageViewer src={file.content} alt={file.name} onClose={() => setViewerOpen(false)} />
        )}
      </>
    );
  }

  // Non-image with content → v1 download/open-in-new-tab semantics, unchanged.
  if (file.content) {
    return (
      <a
        href={file.content}
        target="_blank"
        rel="noopener noreferrer"
        {...pressHandlers}
        style={{ ...baseStyle, ...pressStyle }}
      >
        {inner}
      </a>
    );
  }

  return <div style={baseStyle}>{inner}</div>;
}

export default ReportFileChip;
